import { Link } from "react-router-dom";
import { LegalPage, Mail, Section } from "@/components/LegalPage";

export default function Privacy() {
  return (
    <LegalPage title="Privacy Policy" testid="privacy-page">
      <p className="mt-8 text-lg leading-relaxed text-[#4A3B32]">
        Voices 2 Remember ("we", "us") turns family videos into QR-code keepsakes. This policy explains what we collect, why, who we share it with, and the choices you have. We are based in Florida, United States.
      </p>
      <Section title="What we collect">
        <ul>
          <li><b>Account details</b> from Google sign-in: your name, email address and profile picture.</li>
          <li><b>Contact and shipping details</b>: phone number and the shipping address you enter.</li>
          <li><b>Your video</b>, plus the title and message you add to your keepsake.</li>
          <li><b>Order details</b>: items, prices, discount or referral codes, and order status.</li>
          <li><b>Payment details</b> are entered on Stripe's secure page. We never see or store your full card number.</li>
          <li><b>Basic site activity</b>: pages visited, how you found us (for example a search engine or an affiliate's link) and a random visitor ID. We do not use advertising trackers.</li>
        </ul>
      </Section>
      <Section title="How we use it">
        <ul>
          <li>To make, print and ship your keepsake, and to email you order and shipping updates.</li>
          <li>To post your video to YouTube as an <b>unlisted</b> Short so your QR code can play it.</li>
          <li>To give credit to affiliates who referred you, and to prevent fraud and abuse.</li>
          <li>To understand which pages help families and improve our service.</li>
        </ul>
        <p>We do not sell or rent your personal information.</p>
      </Section>
      <Section title="Who we share it with">
        <p>Only the service providers we need to fill your order:</p>
        <ul>
          <li><b>YouTube (Google)</b>: hosts your video as an unlisted Short on our channel.</li>
          <li><b>Printify</b> and its print partners: receive your print files, name and shipping address to make and ship your items.</li>
          <li><b>Stripe</b>: processes your payment.</li>
          <li><b>Our email provider</b>: sends order and shipping emails.</li>
          <li><b>Our hosting and storage providers</b>: run this website and store your uploaded video.</li>
        </ul>
        <p>We may also share information if the law requires it, or to protect our customers and our business.</p>
      </Section>
      <Section title="Who can watch your video">
        <p>
          Unlisted videos don't appear in YouTube search, but <b>anyone who has the link or scans your QR code can watch and share it</b>. Please only upload videos that everyone in them is comfortable sharing.
        </p>
      </Section>
      <Section title="Removing your video">
        <p>
          You can remove your video anytime from <Link className="font-semibold text-[#A83232] underline" to="/orders" data-testid="privacy-orders-link">My keepsakes</Link>. We delete it from YouTube and from our storage, and printed QR codes will no longer play it. This can't be undone.
        </p>
      </Section>
      <Section title="How long we keep it">
        <p>
          We keep your video for as long as you want your QR codes to work, unless you remove it. We keep order and payment records for as long as tax and accounting laws require.
        </p>
      </Section>
      <Section title="Cookies">
        <p>
          We use a sign-in cookie to keep you logged in, and a referral cookie for up to 30 days when you arrive through an affiliate's link. Site activity uses a random ID stored in your browser.
        </p>
      </Section>
      <Section title="Your choices">
        <p>
          You can ask us to see, correct or delete your personal information. Email <Mail /> and we'll reply within 30 days. Some order records may need to be kept to meet legal requirements.
        </p>
      </Section>
      <Section title="Children">
        <p>
          Our service is for adults 18 and older. Children may appear in family videos only with the permission of a parent or guardian. We don't knowingly collect personal information from children under 13.
        </p>
      </Section>
      <Section title="Security">
        <p>We use secure connections, Google sign-in and trusted providers to protect your information. No system is perfect, but we work to keep your data safe.</p>
      </Section>
      <Section title="Changes and contact">
        <p>If we update this policy, we'll change the date at the top. Questions? Email <Mail />.</p>
      </Section>
    </LegalPage>
  );
}
